import React, { useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { IScheduleFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import * as FormUI from '../../pageForm/FormUI';
import Table from '../../../components/Table';
import { scheduleAnalysisColumnsConfig } from './tableStylesConfig';
import GeneralFormItem from '../../../components/GeneralFormItem';
import {
  ScheduleAlertState,
  ScheduleHistory,
  ScheduleRecipients,
  ScheduleRecurrence,
} from '../components';
import TimezonePopup from '../../../components/TimezonePopup';
import ActionsMenu from '../../../components/ActionsMenu';
import { useScheduleModalHandlers } from '../utils';
import { useNavigate, Link } from 'react-router-dom';
import SchedulePauseResumeMenuOption from '../components/SchedulePauseResumeMenuOption';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { PermissionKey } from '../../../utils/apollo/graphqlSchema';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { assertCondition } from '../../../utils/types';
import { globalFormat } from '../../../utils/formatting';
import ExecutionHistoryPopover from '../../../components/ExecutionHistoryPopover';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';
import { getQueryTypePagePath } from '../../../utils';

interface IProps {
  schedules: IScheduleFieldsFragment[];
}

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: false,
    },
  ],
};

const columnHelper = createColumnHelper<IScheduleFieldsFragment>();

const ScheduleAnalysisTable = ({ schedules }: IProps) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const navigate = useNavigate();
  const { checkIsPermitted } = usePermission();
  const isPermittedToEditSchedule = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AnalysisScheduleEdit],
  });
  const isPermittedToDisableSchedule = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AnalysisScheduleDelete],
  });

  const {
    handleOpenExecuteScheduleModal,
    handleOpenDeleteScheduleModal,
    handleOpenEditScheduleModal,
  } = useScheduleModalHandlers();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Schedule Name',
        cell: (info) => {
          const schedule = info.row.original;
          assertCondition(
            schedule.entity.__typename === 'AnalysisScheduleEntity',
          );
          const analysisPath = getQueryTypePagePath(
            schedule.entity.item.queryType,
            schedule.entity.item.id,
          );
          const analysisName = schedule.entity.item.display;

          return (
            <GeneralFormItem
              label={
                <>
                  {schedule.name} (<Link to={analysisPath}>{analysisName}</Link>
                  )
                </>
              }
              tooltipContent={schedule.name}
              isBold
            />
          );
        },
        meta: scheduleAnalysisColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const schedule = info.row.original;
          return schedule.description ? (
            <GeneralFormItem
              label={schedule.description}
              tooltipContent={schedule.description}
            />
          ) : null;
        },
        meta: scheduleAnalysisColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('recipients', {
        id: 'recipients',
        header: 'RCPT(s)',
        cell: (info) => {
          const schedule = info.row.original;
          return (
            <ScheduleRecipients
              recipients={schedule.recipients}
              emailList={schedule.emailList}
              slackDestination={schedule.slackDestination}
            />
          );
        },
        meta: scheduleAnalysisColumnsConfig.recipients,
        enableSorting: false,
      }),
      columnHelper.accessor('alertOnly', {
        id: 'alertOnly',
        header: 'Alert Only',
        cell: (info) => {
          const schedule = info.row.original;
          return <ScheduleAlertState isActive={schedule.alertOnly} />;
        },
        meta: scheduleAnalysisColumnsConfig.alertState,
        enableSorting: true,
      }),
      columnHelper.accessor('cron', {
        id: 'recurrence',
        header: 'Recurrence',
        cell: (info) => {
          const schedule = info.row.original;
          return (
            <ScheduleRecurrence
              cron={schedule.cron}
              timezone={schedule.timezone}
            />
          );
        },
        meta: scheduleAnalysisColumnsConfig.recurrence,
        enableSorting: false,
      }),
      columnHelper.accessor('nextRunAt', {
        id: 'nextRun',
        header: 'Next Execution',
        cell: (info) => {
          const schedule = info.row.original;
          const browserTimeZone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          return schedule.nextRunAt ? (
            <FormUI.DataLabel>
              <TimezonePopup
                timestamp={schedule.nextRunAt}
                placement="bottom-end"
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                timezone={browserTimeZone}
                showTimezone
              />
            </FormUI.DataLabel>
          ) : null;
        },
        meta: scheduleAnalysisColumnsConfig.nextRun,
        enableSorting: true,
      }),
      columnHelper.accessor('lastRunAt', {
        id: 'lastRun',
        header: 'Last Execution',
        cell: (info) => {
          const schedule = info.row.original;
          const browserTimeZone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          return schedule.lastRunAt ? (
            <FormUI.DataLabel>
              <TimezonePopup
                timestamp={schedule.lastRunAt}
                placement="bottom-end"
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                timezone={browserTimeZone}
                showTimezone
              />
            </FormUI.DataLabel>
          ) : null;
        },
        meta: scheduleAnalysisColumnsConfig.lastRun,
        enableSorting: true,
      }),
      columnHelper.accessor('id', {
        id: 'actions',
        header: () => null,
        cell: (info) => {
          const schedule = info.row.original;
          assertCondition(
            schedule.entity.__typename === 'AnalysisScheduleEntity',
          );
          const analysisPath = getQueryTypePagePath(
            schedule.entity.item.queryType,
            schedule.entity.item.id,
          );

          const handleChangeLocation = () => {
            navigate(analysisPath);
          };

          return (
            <FormUI.ActionsContainer>
              <ExecutionHistoryPopover>
                <ScheduleHistory scheduleId={schedule.id} />
              </ExecutionHistoryPopover>
              <ActionsMenu strategy="fixed">
                <SingleSelectOption
                  onClick={handleChangeLocation}
                  iconName="arrow-square-up-right"
                  label={'Go to Report'}
                />
                <PermissionElementWrapper
                  isPermitted={isPermittedToEditSchedule}
                  customTooltip={
                    schedule.paused
                      ? 'Cannot execute a paused schedule.'
                      : undefined
                  }
                >
                  <SingleSelectOption
                    onClick={() => handleOpenExecuteScheduleModal(schedule)}
                    iconName="refresh"
                    label={'Execute Now'}
                    disabled={schedule.paused || !isPermittedToEditSchedule}
                  />
                </PermissionElementWrapper>
                <PermissionElementWrapper
                  isPermitted={isPermittedToEditSchedule}
                >
                  <SingleSelectOption
                    onClick={() => handleOpenEditScheduleModal(schedule)}
                    iconName="pencil"
                    label={'Edit'}
                    disabled={!isPermittedToEditSchedule}
                  />
                </PermissionElementWrapper>
                <SchedulePauseResumeMenuOption
                  schedule={schedule}
                  dictionaryType={DictionaryTypeEnum.ScheduleAnalysis}
                  requiredPermissionKeys={[PermissionKey.AnalysisScheduleEdit]}
                />
                <PermissionElementWrapper
                  isPermitted={isPermittedToDisableSchedule}
                >
                  <SingleSelectOption
                    onClick={() => handleOpenDeleteScheduleModal(schedule)}
                    iconName="trash"
                    label={'Delete'}
                    disabled={!isPermittedToDisableSchedule}
                  />
                </PermissionElementWrapper>
              </ActionsMenu>
            </FormUI.ActionsContainer>
          );
        },
        meta: scheduleAnalysisColumnsConfig.actions,
        enableSorting: false,
      }),
    ],
    [
      dateFormat,
      timeFormat,
      isPermittedToEditSchedule,
      isPermittedToDisableSchedule,
      handleOpenExecuteScheduleModal,
      handleOpenDeleteScheduleModal,
      handleOpenEditScheduleModal,
      navigate,
    ],
  );

  const table = useReactTable({
    data: schedules,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
      getIsDisabled={(row) => row.paused}
    />
  );
};

export default ScheduleAnalysisTable;
